import React from 'react'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import {
  AcademicCurriculumSection,
  ScheduleTimingSection,
  OrganizationScopeSection,
  FacultyVenueSection,
} from '../../../components/education-form'
import LectureTipsCard from '../components/LectureTipsCard'
import useLectureEntryScreen from './hooks/useLectureEntryScreen'
import { appToast } from '../../../components/AppToast'

export default function LectureEntryScreen() {
  const { state, actions } = useLectureEntryScreen()
  const { form, isEdit, options, isSaving } = state

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-20">
      {/* ── Header Title & Breadcrumb ── */}
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Education Operations</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Lecture' : 'Add New Lectures'}
          </h1>
        </div>
      </section>

      {/* ── Main 2-Column Grid: 4 Form Cards (8 cols) + Sticky TIPS Panel (4 cols) ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
        
        {/* ── Left Column: 4 Categorized Modular Cards ── */}
        <div className="lg:col-span-8 flex flex-col gap-6">

          {/* ── Card 1: Academic & Curriculum ── */}
          <AcademicCurriculumSection
            academicYear={form.academicYear}
            onAcademicYearChange={(val) => actions.patchForm('academicYear', val)}
            academicYearOptions={options.academicYears}
            showIgnoreDuplication={true}
            ignoreDuplication={form.ignoreDuplication}
            onIgnoreDuplicationChange={(val) => actions.patchForm('ignoreDuplication', val)}
            course={form.course}
            onCourseChange={(val) => actions.patchForm('course', val)}
            courseOptions={options.courses}
            subject={form.subject}
            onSubjectChange={(val) => actions.patchForm('subject', val)}
            subjectOptions={options.subjects}
            showChapterNo={true}
            chapterNo={form.chapterNo}
            onChapterNoChange={(val) => actions.patchForm('chapterNo', val)}
            onSelectChapter={() => appToast.info('Opening syllabus chapters selector...')}
            showTopic={true}
            topic={form.topic}
            onTopicChange={(val) => actions.patchForm('topic', val)}
          />

          {/* ── Card 2: Schedule & Timing ── */}
          <ScheduleTimingSection
            showDate={true}
            date={form.date}
            onDateChange={(val) => actions.patchForm('date', val)}
            showTillDate={true}
            tillDate={form.tillDate}
            onTillDateChange={(val) => actions.patchForm('tillDate', val)}
            showTime={true}
            planHour={form.planHour}
            planMinute={form.planMinute}
            planAmPm={form.planAmPm}
            onPlanHourChange={(val) => actions.patchForm('planHour', val)}
            onPlanMinuteChange={(val) => actions.patchForm('planMinute', val)}
            onPlanAmPmChange={(val) => actions.patchForm('planAmPm', val)}
            tillHour={form.tillHour}
            tillMinute={form.tillMinute}
            tillAmPm={form.tillAmPm}
            onTillHourChange={(val) => actions.patchForm('tillHour', val)}
            onTillMinuteChange={(val) => actions.patchForm('tillMinute', val)}
            onTillAmPmChange={(val) => actions.patchForm('tillAmPm', val)}
          />

          {/* ── Card 3: Organization & Target Scope ── */}
          <OrganizationScopeSection
            company={form.company}
            onCompanyChange={(val) => actions.patchForm('company', val)}
            companyOptions={options.companies}
            showCompanyScope={true}
            companyScope={form.companyScope}
            onCompanyScopeChange={(val) => actions.patchForm('companyScope', val)}
            branch={form.branch}
            onBranchChange={(val) => actions.patchForm('branch', val)}
            branchOptions={options.branches}
            showBranchScope={true}
            branchScope={form.branchScope}
            onBranchScopeChange={(val) => actions.patchForm('branchScope', val)}
            batch={form.batch}
            onBatchChange={(val) => actions.patchForm('batch', val)}
            batchOptions={options.batches}
            showBatchScope={true}
            batchScope={form.batchScope}
            onBatchScopeChange={(val) => actions.patchForm('batchScope', val)}
            school={form.schoolCollege}
            onSchoolChange={(val) => actions.patchForm('schoolCollege', val)}
            schoolOptions={options.schools}
            showSchoolScope={true}
            schoolScope={form.schoolCollegeScope}
            onSchoolScopeChange={(val) => actions.patchForm('schoolCollegeScope', val)}
          />

          {/* ── Card 4: Faculty, Venue & Delivery ── */}
          <FacultyVenueSection
            faculty={form.faculty}
            onFacultyChange={(val) => actions.patchForm('faculty', val)}
            facultyOptions={options.faculty}
            showExtraLecture={true}
            extraLecture={form.extraLecture}
            onExtraLectureChange={(val) => actions.patchForm('extraLecture', val)}
            classroom={form.location}
            onClassroomChange={(val) => actions.patchForm('location', val)}
            classroomOptions={options.locations}
            showVideoUrl={true}
            videoUrl={form.videoUrl}
            onVideoUrlChange={(val) => actions.patchForm('videoUrl', val)}
          />

        </div>

        {/* ── Right Column: Sticky TIPS & Instructions Panel (4 cols) ── */}
        <div className="lg:col-span-4 sticky top-6">
          <LectureTipsCard />
        </div>

      </div>

      {/* ── Global Sticky Form Footer Bar ── */}
      <AppFormFooterBar
        cancelPath="/lectures/list"
        isSaving={isSaving}
        showSaveAndContinue={!isEdit}
        onSaveContinue={() => actions.handleSave(true)}
        onSaveExit={() => actions.handleSave(false)}
        saveLabel={isEdit ? 'Update Lecture' : 'Save & Exit'}
      />
    </div>
  )
}
